import { CommonModule } from '@angular/common';
import { Component, inject, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { ItemComponent, ButtonComponent, TileComponent, TagComponent } from '@tecalisdev/infinity';
import {
	IOperationDetailResponse,
	IOperationFormDetail,
	OPERATION_TYPE,
} from '../../../operations/models/operations.models';
import {
	buildFormOperationChecksDetail,
	resolveFormOperationOcrPayload,
} from '../../../operations/utils/form-operation-detail-contract.util';
import {
	hasFormOperationMatchDataKo,
	resolveOperationDetailEffectiveState,
} from '../../../operations/utils/form-operation-match-data.util';
import { listFormMatchCompareFields } from '../../../operations/utils/form-operation-match-compare.util';
import { MasterService } from '../../../shared/services/master.service';
import { NavigationService } from '../../../shared/services/navigation.service';
import { ProcessHelperService } from '../../services/process-helper.service';
import {
	IOperationDocumentsDetail,
	IOperationEvaluationDetail,
	IOperationOcrDetail,
	IOperationPolDetail,
	IProcessDetailResponse,
	PROCESS_STATUS,
} from '../../models/processes.models';
import { ChecksComponent } from '../checks/checks.component';

/**
 * Muestra el bloque RESULT (KO / PENDING + revisión + comprobaciones KO, o motivo de estado) en detalle de proceso u operación.
 * También se muestra si hay `status_reasons` o `actions.KO` aunque el estado no sea KO/PENDING (p. ej. OK con motivo).
 * - Proceso: pasar [processDetail].
 * - Operación: pasar [operationDetail]; se arma un snapshot compatible con app-checks según el tipo.
 */
@Component({
	selector: 'app-detail-result-tile',
	imports: [
		CommonModule,
		TranslatePipe,
		TileComponent,
		ButtonComponent,
		ChecksComponent,
		ItemComponent,
		TagComponent,
	],
	templateUrl: './detail-result-tile.component.html',
	styleUrl: './detail-result-tile.component.scss',
})
export class DetailResultTileComponent implements OnInit, OnChanges {
	@Input() processDetail: IProcessDetailResponse | null = null;
	@Input() operationDetail: IOperationDetailResponse | null = null;
	/** Oculta el CTA «Revisar» y el disclaimer PENDING; en review muestra checks KO / motivos. */
	@Input() hideReviewAction = false;

	public masterService = inject(MasterService);
	public PROCESS_STATUS = PROCESS_STATUS;
	public OPERATION_TYPE = OPERATION_TYPE;

	protected processUuid = '';
	protected statusKey = '';
	protected checksDetail: IProcessDetailResponse | null = null;
	protected formMatchKoMessage: { title: string; subtitle: string } | null = null;
	protected processMatchKoMessage: { title: string; subtitle: string } | null = null;

	/**
	 * Subtítulo y cuerpo de requisitos KO: hay checks/mensajes KO que listar (NIF-819: sin filtrar por estado).
	 * El motivo manual es opcional y va en su bloque.
	 */
	protected get showKoDisclaimer(): boolean {
		return this.hasFailedChecksContent;
	}

	/**
	 * Visibilidad del tile:
	 * - NIF-833: en review PENDING solo si hay fallos o motivos.
	 * - NIF-819: también si hay `actions.KO` aunque el estado sea OK.
	 */
	protected get showResultTile(): boolean {
		const hasStatusReasons = !!(this.operationDetail?.status_reasons || this.processDetail?.status_reasons);
		if (hasStatusReasons) {
			return true;
		}
		if (this.hasFailedChecksContent) {
			return true;
		}
		if (!this.processUuid) {
			return false;
		}
		if (this.statusKey === PROCESS_STATUS.KO) {
			return true;
		}
		if (this.statusKey === PROCESS_STATUS.PENDING) {
			return !this.hideReviewAction;
		}
		return false;
	}

	private get hasFailedChecksContent(): boolean {
		if (this.formMatchKoMessage) {
			return true;
		}
		if (this.processMatchKoMessage && !this.checksDetail?.data?.OCR?.actions?.KO) {
			return true;
		}
		const detail = this.checksDetail;
		if (!detail) {
			return false;
		}
		return !!(
			this.processHelper.getKoCompletionExclusiveMessageForOcr(detail) ||
			this.processHelper.getKoCompletionExclusiveMessageForPol(detail) ||
			this.processHelper.shouldShowOperationKoReason(detail.data?.OCR) ||
			this.processHelper.shouldShowOperationKoReason(detail.data?.POL) ||
			this.processHelper.shouldShowOperationKoReason(detail.data?.ATTORNEY_POWERS) ||
			this.processHelper.shouldShowOperationKoReason(detail.data?.DOCUMENTS)
		);
	}

	/** Ops con estado propio a etiquetar en RESULT (ignora status_process del proceso). */
	protected get operationStateTags(): { type: OPERATION_TYPE; state: string }[] {
		if (!this.showKoDisclaimer) {
			return [];
		}
		const detail = this.checksDetail;
		if (!detail?.data) {
			return [];
		}
		const tags: { type: OPERATION_TYPE; state: string }[] = [];
		const ocrState = detail.data.OCR?.state;
		if (ocrState) {
			tags.push({ type: OPERATION_TYPE.OCR, state: String(ocrState).toUpperCase() });
		}
		const polState = detail.data.POL?.state;
		if (polState) {
			tags.push({ type: OPERATION_TYPE.POL, state: String(polState).toUpperCase() });
		}
		return tags;
	}

	public processHelper = inject(ProcessHelperService);
	private readonly navigationService = inject(NavigationService);

	public ngOnInit(): void {
		this.resolve();
	}

	/** Navega a la revisión manual del proceso pendiente. */
	public goToReview(event?: Event): void {
		if (!this.processUuid) return;
		this.navigationService.navigateTo({ routeSegments: ['processes', 'review', this.processUuid] }, event);
	}

	public ngOnChanges(changes: SimpleChanges): void {
		if (changes['processDetail'] || changes['operationDetail']) {
			this.resolve();
		}
	}

	private resolve(): void {
		this.formMatchKoMessage = null;
		this.processMatchKoMessage = null;

		if (this.processDetail) {
			this.processUuid = this.processDetail.process_uuid ?? '';
			this.statusKey = this.processDetail.status_process ?? '';
			this.checksDetail = this.processDetail;
			this.processMatchKoMessage = this.processHelper.resolveProcessMatchDataKoMessage(this.processDetail);
			return;
		}
		if (this.operationDetail) {
			this.processUuid = this.operationDetail.process_uuid ?? '';
			this.statusKey = resolveOperationDetailEffectiveState(this.operationDetail) ?? '';
			const snapshot = this.buildOperationChecksSnapshot(this.operationDetail);
			this.checksDetail = snapshot;
			if (!snapshot && this.operationDetail.operation_type_name === OPERATION_TYPE.FORM) {
				const form = this.operationDetail.data as unknown as IOperationFormDetail;
				if (
					!hasFormOperationMatchDataKo(form) &&
					(this.statusKey === PROCESS_STATUS.KO ||
						this.statusKey === PROCESS_STATUS.PENDING ||
						this.processHelper.shouldShowOperationKoReason(form as { actions?: { KO?: unknown } }))
				) {
					this.formMatchKoMessage = this.processHelper.detailKoGeneral(2014, OPERATION_TYPE.FORM);
				}
			}
			return;
		}
		this.processUuid = '';
		this.statusKey = '';
		this.checksDetail = null;
	}

	private buildOperationChecksSnapshot(detail: IOperationDetailResponse): IProcessDetailResponse | null {
		const type = detail.operation_type_name;
		const effectiveState = resolveOperationDetailEffectiveState(detail) ?? PROCESS_STATUS.OK;
		const base: Pick<
			IProcessDetailResponse,
			'general' | 'process_uuid' | 'process_details' | 'status_process' | 'locks'
		> = {
			general: {
				profile: { uuid: '', alias: '' },
				sms: [],
				auth: '',
				company: '',
				company_uuid: '',
				location: [],
				devices: [],
				init_operation: '',
				total_time: 0,
				end_operation: '',
				validation: '',
			},
			process_uuid: detail.process_uuid ?? '',
			process_details: '',
			status_process: effectiveState as PROCESS_STATUS,
			locks: detail.locks,
		};

		switch (type) {
			case OPERATION_TYPE.OCR: {
				const ocr = detail.data as unknown as IOperationOcrDetail;
				this.processHelper.setYearsOld(ocr);
				return {
					...base,
					data: { OCR: ocr } as IProcessDetailResponse['data'],
				} as IProcessDetailResponse;
			}
			case OPERATION_TYPE.POL: {
				const pol = detail.data as unknown as IOperationPolDetail;
				return {
					...base,
					data: { POL: pol } as IProcessDetailResponse['data'],
				} as IProcessDetailResponse;
			}
			case OPERATION_TYPE.DOCUMENTS: {
				const documents = detail.data as unknown as IOperationDocumentsDetail;
				return {
					...base,
					data: { DOCUMENTS: documents } as IProcessDetailResponse['data'],
				} as IProcessDetailResponse;
			}
			case OPERATION_TYPE.CONNECTIONS:
			case OPERATION_TYPE.FINANCIAL_EVALUATION:
			case OPERATION_TYPE.EVALUATION: {
				const evaluation = detail.data as unknown as IOperationEvaluationDetail;
				const isAttorneyPowers = evaluation?.type?.toUpperCase() === 'SCRIPTURE';
				return {
					...base,
					data: (isAttorneyPowers
						? { ATTORNEY_POWERS: evaluation }
						: { EVALUATION: evaluation }) as IProcessDetailResponse['data'],
				} as IProcessDetailResponse;
			}
			case OPERATION_TYPE.ATTORNEY_POWERS: {
				const evaluation = detail.data as unknown as IOperationEvaluationDetail;
				return {
					...base,
					data: { ATTORNEY_POWERS: evaluation } as IProcessDetailResponse['data'],
				} as IProcessDetailResponse;
			}
			case OPERATION_TYPE.FORM: {
				const form = detail.data as unknown as IOperationFormDetail;
				const ocr = resolveFormOperationOcrPayload(form);
				const hasMatchCompare = listFormMatchCompareFields(form).length > 0;
				if (!ocr && !hasMatchCompare) {
					return null;
				}
				if (ocr) {
					this.processHelper.setYearsOld(ocr);
				}
				const built = buildFormOperationChecksDetail(detail);
				return built
					? ({
							...built,
							...base,
							status_process: effectiveState as PROCESS_STATUS,
						} as IProcessDetailResponse)
					: null;
			}
			default:
				return null;
		}
	}
}
